import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Edit3, Plus, Trash2, Clock } from 'lucide-react';
import {
    MaintenanceTemplate,
    TemplatesByType,
    ComponentLifetime,
    EditForm,
    DirectionalInterval,
    DirectionalLifetime
} from './types';
import {BuildComponentType, CardinalDirection, MaintenanceIntervalType} from "../../../types/Enums";
import {translate} from "../../../types/EnumTranslations";
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import { maintenanceTemplateApi } from '../../../api/maintenanceTemplateApi';
import { translateIntervalType } from '../../../Helpers/maintenanceCostUtils';
import LifetimeEditModal from './LifetimeEditModal';
import TemplateEditModal from './TemplateEditModal';
import { isTemplateArchived } from '../../../Helpers/maintenanceTemplates';

interface ComponentTypeSectionProps {
    componentType: BuildComponentType;
    templates: TemplatesByType;
    setTemplates: React.Dispatch<React.SetStateAction<TemplatesByType>>;
    componentLifetimes: Record<string, ComponentLifetime>;
    setComponentLifetimes: React.Dispatch<React.SetStateAction<Record<string, ComponentLifetime>>>;
    expandedComponentSections: Set<string>;
    editingTemplate: string | null;
    editingLifetime: string | null;
    saving: boolean;
    toggleComponentSection: (componentType: string) => void;
    setEditingTemplate: React.Dispatch<React.SetStateAction<string | null>>;
    setEditingLifetime: React.Dispatch<React.SetStateAction<string | null>>;
    setSaving: React.Dispatch<React.SetStateAction<boolean>>;
}

const ComponentTypeSection: React.FC<ComponentTypeSectionProps> = ({
                                                                       componentType,
                                                                       templates,
                                                                       setTemplates,
                                                                       componentLifetimes,
                                                                       setComponentLifetimes,
                                                                       expandedComponentSections,
                                                                       editingTemplate,
                                                                       editingLifetime,
                                                                       saving,
                                                                       toggleComponentSection,
                                                                       setEditingTemplate,
                                                                       setEditingLifetime,
                                                                       setSaving,
                                                                   }) => {
    const { getTranslation: getComponentTypeTranslation, getBaseType } = useBuildComponentTypes();
    const [showLifetimeModal, setShowLifetimeModal] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [editingTemplateData, setEditingTemplateData] = useState<MaintenanceTemplate | null>(null);
    const [isNewTemplate, setIsNewTemplate] = useState(false);

    // Archived templates are managed under Inställningar › Komponenttyper
    const componentTemplates = (templates[componentType] || []).filter(t => !isTemplateArchived(t));
    const isComponentExpanded = expandedComponentSections.has(componentType);
    const templateCount = componentTemplates.length;
    const currentLifetime = componentLifetimes[componentType];

    const openLifetimeModal = (): void => {
        setShowLifetimeModal(true);
    };

    const closeLifetimeModal = (): void => {
        setShowLifetimeModal(false);
    };

    const openTemplateModal = (template?: MaintenanceTemplate): void => {
        if (template) {
            setEditingTemplateData(template);
            setIsNewTemplate(false);
        } else {
            setEditingTemplateData(null);
            setIsNewTemplate(true);
        }
        setShowTemplateModal(true);
    };

    const closeTemplateModal = (): void => {
        setShowTemplateModal(false);
        setEditingTemplateData(null);
        setIsNewTemplate(false);
    };

    const handleSaveTemplate = async (formData: EditForm): Promise<void> => {
        try {
            setSaving(true);

            // Ensure primaryComponentType is set
            const formWithType: EditForm = {
                ...formData,
                primaryComponentType: formData.primaryComponentType || componentType
            };

            if (isNewTemplate) {
                const newId = await maintenanceTemplateApi.createTemplate(formWithType);

                const newTemplate: MaintenanceTemplate = {
                    id: newId,
                    name: formWithType.name,
                    description: formWithType.description || '',
                    interval_type: formWithType.interval_type,
                    interval_value: formWithType.interval_value,
                    cost: formWithType.cost,
                    cost_unit: formWithType.cost_unit,
                    primaryComponentType: formWithType.primaryComponentType,
                    ...(formWithType.directionalIntervals?.length > 0 && {
                        directionalIntervals: formWithType.directionalIntervals
                    }),
                    ...(formWithType.buildingKey && { buildingKey: formWithType.buildingKey })
                };

                setTemplates(prev => ({
                    ...prev,
                    [componentType]: [...(prev[componentType] || []), newTemplate]
                }));
            } else if (editingTemplateData) {
                await maintenanceTemplateApi.updateTemplate(editingTemplateData.id, formWithType, { source: 'templatesEditor' });

                setTemplates(prev => ({
                    ...prev,
                    [componentType]: (prev[componentType] || []).map(t =>
                        t.id === editingTemplateData.id ? { ...t, ...formWithType, id: t.id } : t
                    )
                }));
            }

            closeTemplateModal();
        } catch (error: any) {
            console.error('Error saving template:', error);
            alert(error.message || 'Fel vid sparande av mall');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveLifetime = async (lifetime: ComponentLifetime): Promise<void> => {
        try {
            setSaving(true);

            const cleanedData = await maintenanceTemplateApi.saveLifetime(componentType, lifetime, { source: 'templatesEditor' });

            setComponentLifetimes(prev => ({ ...prev, [componentType]: cleanedData }));
            closeLifetimeModal();
        } catch (error) {
            console.error('Error saving lifetime:', error);
            alert(error instanceof Error ? error.message : 'Fel vid sparande av livslängd');
        } finally {
            setSaving(false);
        }
    };


    const deleteTemplate = async (componentType: BuildComponentType, templateId: string): Promise<void> => {
        if (!window.confirm('Är du säker på att du vill radera denna mall?')) return;

        try {
            setSaving(true);
            await maintenanceTemplateApi.deleteTemplate(templateId);

            setTemplates(prev => ({
                ...prev,
                [componentType]: prev[componentType].filter(t => t.id !== templateId),
            }));
        } catch (error: any) {
            console.error('Error deleting template:', error);
            alert(error.message || 'Fel vid radering av mall');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mb-3">
            <div className="card border-0 shadow-sm">
                {/* Component Type Header */}
                <div className="card-header bg-white border-bottom-0 p-0">
                    <button
                        className="btn btn-link w-100 text-start text-decoration-none p-3 rounded-0"
                        onClick={() => toggleComponentSection(componentType)}
                    >
                        <div className="d-flex align-items-center">
                            {isComponentExpanded ? (
                                <ChevronDown className="me-3" size={18} style={{color: '#6c757d'}} />
                            ) : (
                                <ChevronRight className="me-3" size={18} style={{color: '#6c757d'}} />
                            )}
                            <span className="fw-medium text-dark fs-6">
                                {getComponentTypeTranslation(componentType)}
                                <span className="badge bg-light text-secondary ms-2 small">{templateCount}</span>
                            </span>
                        </div>
                    </button>
                </div>

                {/* Component Content */}
                {isComponentExpanded && (
                    <div className="card-body p-3">
                        {/* Expected Lifetime Section */}
                        <div className="mb-4">
                            <div className="card lifetime-card">
                                <div className="card-body p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div className="d-flex align-items-center">
                                            <Clock className="me-2" size={18} style={{color: '#6c757d'}} />
                                            <h6 className="mb-0 fw-semibold" style={{color: '#495057'}}>Beräknad livslängd</h6>
                                        </div>
                                        <button
                                            onClick={openLifetimeModal}
                                            className="btn btn-outline-secondary btn-sm"
                                        >
                                            <Edit3 size={14} className="me-1" />
                                            Ändra
                                        </button>
                                    </div>

                                    {/* Lifetime Display */}
                                    <div>
                                        {currentLifetime ? (
                                            <div>
                                <span className="fw-medium d-block">
                                    Standard: {currentLifetime.expectedLifetimeYears} år beräknad livslängd
                                </span>
                                                        {currentLifetime.directionalLifetimes && currentLifetime.directionalLifetimes.length > 0 && (
                                                            <div className="mt-1">
                                                                {currentLifetime.directionalLifetimes.map(dl => (
                                                                    <span key={dl.direction} className="badge bg-info me-1 small">
                                                {translate(dl.direction)}: {dl.expectedLifetimeYears} år
                                                                        {dl.replacementCost && dl.replacementCost !== currentLifetime.replacementCost &&
                                                                            ` (${dl.replacementCost.toFixed(0)} kr)`}
                                            </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {typeof currentLifetime.replacementCost === 'number' && (
                                                            <span className="text-muted small d-block mt-1">
                                        Standard byteskostnad: {currentLifetime.replacementCost.toFixed(0)} kr {currentLifetime.costUnit === 'per_sqm' ? 'per m²' : currentLifetime.costUnit === 'per_lm' ? 'per lm' : 'per enhet'}
                                    </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted fst-italic">Ingen livslängd angiven</span>
                                                )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Templates Section */}
                        <div className="ps-3">
                            <h6 className="text-muted mb-3">Underhållsmallar</h6>

                            {componentTemplates.map((template) => (
                                <div key={template.id} className="card mb-3 border-0 shadow-sm">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1">
                                                    <h6 className="card-title fw-semibold mb-1">{template.name}</h6>
                                                    {template.description && (
                                                        <p className="card-text text-muted small mb-2">{template.description}</p>
                                                    )}
                                                    <div className="d-flex gap-2 flex-wrap">
                                                        <span className="badge" style={{ backgroundColor: '#28a745', color: '#ffffff' }}>
                                                            Standard: {template.interval_value} {translateIntervalType(template.interval_type)} intervall
                                                        </span>
                                                        {template.directionalIntervals?.map(di => (
                                                            <span key={di.direction} className="badge" style={{ backgroundColor: '#17a2b8', color: '#ffffff' }}>
                                                                {translate(di.direction)}: {di.interval_value} {translateIntervalType(di.interval_type)}
                                                            </span>
                                                        ))}
                                                        <span className="badge" style={{ backgroundColor: '#007bff', color: '#ffffff' }}>
                                                            {template.cost} kr / {template.cost_unit === 'per_sqm' ? 'm²' : template.cost_unit === 'per_lm' ? 'lm' : 'enhet'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="ms-3">
                                                    <div className="btn-group btn-group-sm">
                                                        <button onClick={() => openTemplateModal(template)} className="btn btn-outline-secondary">
                                                            <Edit3 size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => deleteTemplate(componentType as BuildComponentType, template.id)}
                                                            disabled={saving}
                                                            className="btn btn-outline-danger"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                    </div>
                                </div>
                            ))}

                            {/* Add New Template Button */}
                            <button
                                onClick={() => openTemplateModal()}
                                className="btn btn-outline-primary btn-sm"
                            >
                                <Plus size={16} className="me-1" />
                                Lägg till ny mall
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Lifetime Edit Modal */}
            <LifetimeEditModal
                show={showLifetimeModal}
                lifetime={currentLifetime}
                componentType={componentType}
                onSave={handleSaveLifetime}
                onCancel={closeLifetimeModal}
                saving={saving}
            />

            {/* Template Edit Modal */}
            <TemplateEditModal
                show={showTemplateModal}
                template={editingTemplateData}
                componentType={componentType}
                componentTypeKey={componentType}
                isNewTemplate={isNewTemplate}
                onSave={handleSaveTemplate}
                onCancel={closeTemplateModal}
                saving={saving}
                baseType={getBaseType(componentType)}
            />
        </div>
    );
};

export default ComponentTypeSection;